import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle } from "lucide-react";
import { DsButton, DsIcon } from "../../../design-system";

/** Small confirm dialog rendered above the role wizard (z-[10010]; Filters modal is z-[10020]). */
export default function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "danger",
  loading = false,
  onConfirm,
  onCancel,
}) {
  const cancelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    cancelRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) {
        e.stopPropagation();
        onCancel();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, loading, onCancel]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10010] flex items-center justify-center bg-black/50 p-4"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onCancel();
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="urm-confirm-title"
        className="w-full max-w-md rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-2xl"
      >
        <div className="flex items-start gap-3 p-5">
          <span
            className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
              tone === "danger"
                ? "bg-[hsl(var(--ds-danger)/0.12)] text-[hsl(var(--ds-danger))]"
                : "bg-[hsl(var(--ds-warning)/0.18)] text-[hsl(var(--ds-warning-fg))]"
            }`}
          >
            <DsIcon icon={AlertTriangle} size="sm" />
          </span>
          <div className="min-w-0">
            <h3 id="urm-confirm-title" className="ds-h4 text-[hsl(var(--ds-fg))]">
              {title}
            </h3>
            <div className="ds-body mt-1.5 text-[hsl(var(--ds-muted-fg))]">{children}</div>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-5 py-3">
          <DsButton ref={cancelRef} variant="outline" size="sm" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </DsButton>
          <DsButton
            variant={tone === "danger" ? "danger" : "primary"}
            size="sm"
            onClick={onConfirm}
            loading={loading}
          >
            {loading ? "Please wait…" : confirmLabel}
          </DsButton>
        </div>
      </div>
    </div>,
    document.body
  );
}
